import React, { useState, useEffect } from 'react';
import { 
  Shield, AlertTriangle, CheckCircle, Bell, Settings,
  DollarSign, Users, Calendar, TrendingUp, X, Save,
  Clock, FileText, Eye, Download
} from 'lucide-react';
import { CostCalculation, CostUtils } from '../utils/costCalculation';
import { MeetingStorage } from '../utils/meetingStorage';
import { UserStorage } from '../utils/userStorage';
import { StaffStorage } from '../utils/staffStorage';
import { logger } from '../utils/logger';

const ComplianceMonitor = ({ userType = 'sales' }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [complianceSettings, setComplianceSettings] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [monitoringData, setMonitoringData] = useState(null);
  
  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    alertThresholds: {
      warningPercentage: 80, // Warn at 80% of limit
      criticalPercentage: 95, // Critical at 95% of limit
    },
    customLimits: {
      perPersonMeal: 25.00,
      quarterlyPhysician: 500.00,
      annualPhysician: 2000.00,
    },
    notifications: {
      emailAlerts: true,
      realTimeMonitoring: true,
      weeklyReports: true,
      monthlyReports: true,
    },
    autoActions: {
      blockNonCompliantBookings: false,
      requireManagerApproval: true,
      sendComplianceReminders: true,
    }
  });

  useEffect(() => {
    initializeComplianceMonitor();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!refreshing) {
        checkCompliance();
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [refreshing]);

  const initializeComplianceMonitor = async () => {
    setLoading(true);
    
    try {
      const user = UserStorage.getCurrentUser();
      if (!user) {
        setLoading(false);
        return;
      }
      
      setCurrentUser(user);
      
      // Load compliance settings
      const savedSettings = localStorage.getItem('meetingsync_compliance_settings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setComplianceSettings(settings);
        setSettingsForm(settings);
      } else {
        setComplianceSettings(settingsForm);
      }
      
      // Perform initial compliance check
      await checkCompliance();
    } catch (error) {
      logger.error('Error initializing compliance monitor:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCompliance = async () => {
    setRefreshing(true);
    
    try {
      const alerts = [];
      const now = new Date();
      const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
      const currentYear = now.getFullYear();
      
      // Get all meetings for analysis
      let meetings = [];
      if (userType === 'sales') {
        meetings = MeetingStorage.getMeetingsForSalesRep(currentUser.id);
      } else if (userType === 'manager') {
        meetings = MeetingStorage.getAllMeetings();
      } else {
        meetings = MeetingStorage.getMeetingsForCustomer(currentUser.id);
      }
      
      // Check individual meeting compliance
      for (const meeting of meetings) {
        if (meeting.status !== 'completed' && meeting.status !== 'confirmed') continue;
        
        const staffOrders = StaffStorage.getStaffOrdersForMeeting(meeting.id);
        const costData = CostCalculation.calculateMeetingCost(meeting, staffOrders);
        
        if (!costData.complianceStatus.isCompliant) {
          alerts.push({
            id: `meeting-${meeting.id}`,
            type: 'violation',
            severity: 'high',
            title: 'Non-Compliant Meeting',
            message: `Meeting with ${meeting.customer} exceeds compliance limits`,
            details: costData.complianceStatus.violations,
            meetingId: meeting.id,
            timestamp: new Date().toISOString(),
            costPerPerson: costData.averageCostPerPerson,
            totalCost: costData.totalCost
          });
        }
        
        // Check warning thresholds
        const warningThreshold = (complianceSettings?.alertThresholds?.warningPercentage || 80) / 100;
        const maxAllowed = CostCalculation.COMPLIANCE_LIMITS.FEDERAL_MAX_PER_PERSON;
        
        if (costData.averageCostPerPerson > (maxAllowed * warningThreshold) && 
            costData.averageCostPerPerson <= maxAllowed) {
          alerts.push({
            id: `warning-${meeting.id}`,
            type: 'warning',
            severity: 'medium',
            title: 'Approaching Spending Limit',
            message: `Meeting with ${meeting.customer} is ${((costData.averageCostPerPerson / maxAllowed) * 100).toFixed(1)}% of federal limit`,
            meetingId: meeting.id,
            timestamp: new Date().toISOString(),
            costPerPerson: costData.averageCostPerPerson,
            totalCost: costData.totalCost
          });
        }
      }
      
      // Check quarterly spending limits per physician
      const physicianSpending = {};
      meetings.forEach(meeting => {
        const meetingDate = new Date(meeting.date);
        if (meetingDate.getFullYear() !== currentYear) return;
        
        const meetingQuarter = Math.ceil((meetingDate.getMonth() + 1) / 3);
        if (meetingQuarter !== currentQuarter) return;
        
        if (!physicianSpending[meeting.customerId]) {
          physicianSpending[meeting.customerId] = {
            name: meeting.customer,
            totalSpent: 0,
            meetings: 0
          };
        }
        
        physicianSpending[meeting.customerId].totalSpent += parseFloat(meeting.totalCost || 0);
        physicianSpending[meeting.customerId].meetings++;
      });
      
      // Check for physicians approaching quarterly limits
      Object.entries(physicianSpending).forEach(([physicianId, spending]) => {
        const quarterlyLimit = complianceSettings?.customLimits?.quarterlyPhysician || 
                             CostCalculation.COMPLIANCE_LIMITS.QUARTERLY_LIMIT_PER_PHYSICIAN;
        const utilizationPercentage = (spending.totalSpent / quarterlyLimit) * 100;
        
        const warningThreshold = complianceSettings?.alertThresholds?.warningPercentage || 80;
        const criticalThreshold = complianceSettings?.alertThresholds?.criticalPercentage || 95;
        
        if (utilizationPercentage >= criticalThreshold) {
          alerts.push({
            id: `quarterly-critical-${physicianId}`,
            type: 'critical',
            severity: 'high',
            title: 'Quarterly Limit Exceeded',
            message: `${spending.name} has exceeded ${utilizationPercentage.toFixed(1)}% of quarterly spending limit`,
            physicianId,
            physicianName: spending.name,
            currentSpending: spending.totalSpent,
            limit: quarterlyLimit,
            utilizationPercentage,
            timestamp: new Date().toISOString()
          });
        } else if (utilizationPercentage >= warningThreshold) {
          alerts.push({
            id: `quarterly-warning-${physicianId}`,
            type: 'warning',
            severity: 'medium',
            title: 'Approaching Quarterly Limit',
            message: `${spending.name} has used ${utilizationPercentage.toFixed(1)}% of quarterly spending limit`,
            physicianId,
            physicianName: spending.name,
            currentSpending: spending.totalSpent,
            limit: quarterlyLimit,
            utilizationPercentage,
            timestamp: new Date().toISOString()
          });
        }
      });
      
      // Calculate monitoring statistics
      const stats = {
        totalMeetings: meetings.length,
        compliantMeetings: meetings.filter(m => {
          const staffOrders = StaffStorage.getStaffOrdersForMeeting(m.id);
          const costData = CostCalculation.calculateMeetingCost(m, staffOrders);
          return costData.complianceStatus.isCompliant;
        }).length,
        totalViolations: alerts.filter(a => a.type === 'violation').length,
        totalWarnings: alerts.filter(a => a.type === 'warning').length,
        criticalAlerts: alerts.filter(a => a.severity === 'high').length,
        averageComplianceRate: 0,
        lastUpdated: new Date().toISOString()
      };
      
      stats.averageComplianceRate = stats.totalMeetings > 0 ? 
        (stats.compliantMeetings / stats.totalMeetings) * 100 : 100;
      
      setMonitoringData(stats);
      setComplianceAlerts(alerts.slice(0, 50)); // Keep last 50 alerts
      
    } catch (error) {
      logger.error('Error checking compliance:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const saveComplianceSettings = () => {
    try {
      localStorage.setItem('meetingsync_compliance_settings', JSON.stringify(settingsForm));
      setComplianceSettings(settingsForm);
      setShowSettingsModal(false);
      
      // Trigger a compliance recheck with new settings
      checkCompliance();
      
      alert('Compliance settings saved successfully!');
    } catch (error) {
      logger.error('Error saving compliance settings:', error);
      alert('Failed to save settings. Please try again.');
    }
  };

  const dismissAlert = (alertId) => {
    setComplianceAlerts(alerts => alerts.filter(alert => alert.id !== alertId));
  };

  const exportComplianceReport = () => {
    const reportData = {
      generatedAt: new Date().toISOString(),
      user: currentUser.name,
      monitoringPeriod: 'Current Quarter',
      statistics: monitoringData,
      alerts: complianceAlerts,
      settings: complianceSettings
    };
    
    const jsonContent = JSON.stringify(reportData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-monitoring-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getAlertIcon = (alert) => {
    switch (alert.severity) {
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'medium':
        return <Bell className="w-5 h-5 text-yellow-500" />;
      default:
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
    }
  };

  const getAlertBgColor = (alert) => {
    switch (alert.severity) {
      case 'high':
        return 'bg-red-50 border-red-200';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Compliance Monitoring</h2>
            <p className="text-gray-600 mt-1">
              Real-time monitoring of spending limits and compliance violations
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {userType === 'manager' && (
              <button
                onClick={() => setShowSettingsModal(true)}
                className="flex items-center space-x-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </button>
            )}
            <button
              onClick={exportComplianceReport}
              className="flex items-center space-x-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export Report</span>
            </button>
            <button
              onClick={checkCompliance}
              disabled={refreshing}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <Shield className="w-4 h-4" />
              <span>{refreshing ? 'Checking...' : 'Check Now'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Info Banner for Non-Managers */}
      {userType !== 'manager' && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-800">Compliance Monitoring</p>
              <p className="text-sm text-blue-700 mt-1">
                You can view compliance status and alerts here. Compliance thresholds and settings are managed by your assigned manager.
                {!currentUser?.managerId && " You don't currently have an assigned manager - metrics are tracked but no custom limits are enforced."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Monitoring Statistics */}
      {monitoringData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Compliance Rate</p>
                <p className="text-2xl font-bold text-green-600">{monitoringData.averageComplianceRate.toFixed(1)}%</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Violations</p>
                <p className="text-2xl font-bold text-red-600">{monitoringData.totalViolations}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Warnings</p>
                <p className="text-2xl font-bold text-yellow-600">{monitoringData.totalWarnings}</p>
              </div>
              <Bell className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Meetings</p>
                <p className="text-2xl font-bold text-gray-800">{monitoringData.totalMeetings}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>
      )}

      {/* Active Alerts */}
      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">Active Compliance Alerts</h3>
            <span className="text-sm text-gray-500">
              Last updated: {monitoringData?.lastUpdated ? 
                new Date(monitoringData.lastUpdated).toLocaleTimeString() : 'Never'}
            </span>
          </div>
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {complianceAlerts.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">All Clear!</h3>
              <p className="text-gray-600">No active compliance violations or warnings.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {complianceAlerts.map((alert) => (
                <div key={alert.id} className={`p-4 border-l-4 ${getAlertBgColor(alert)}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      {getAlertIcon(alert)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-medium text-gray-800">{alert.title}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            alert.severity === 'high' ? 'bg-red-100 text-red-800' :
                            alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {alert.severity.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-gray-600 mt-1">{alert.message}</p>
                        
                        {alert.costPerPerson && (
                          <div className="mt-2 text-sm text-gray-500">
                            Cost per person: {CostUtils.formatCurrency(alert.costPerPerson)} | 
                            Total: {CostUtils.formatCurrency(alert.totalCost)}
                          </div>
                        )}
                        
                        {alert.utilizationPercentage && (
                          <div className="mt-2 text-sm text-gray-500">
                            Quarterly utilization: {alert.utilizationPercentage.toFixed(1)}% | 
                            Spent: {CostUtils.formatCurrency(alert.currentSpending)} / {CostUtils.formatCurrency(alert.limit)}
                          </div>
                        )}
                        
                        {alert.details && alert.details.length > 0 && (
                          <div className="mt-2">
                            <details className="text-sm">
                              <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                                View Details
                              </summary>
                              <div className="mt-2 pl-4 border-l-2 border-gray-200">
                                {alert.details.map((detail, idx) => (
                                  <p key={idx} className="text-gray-600">{detail.message}</p>
                                ))}
                              </div>
                            </details>
                          </div>
                        )}
                        
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(alert.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => dismissAlert(alert.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Compliance Monitoring Settings</h3>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Alert Thresholds */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Alert Thresholds</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Warning Threshold (%)
                      </label>
                      <input
                        type="number"
                        min="50"
                        max="99"
                        value={settingsForm.alertThresholds.warningPercentage}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          alertThresholds: {
                            ...settingsForm.alertThresholds,
                            warningPercentage: parseInt(e.target.value)
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Critical Threshold (%)
                      </label>
                      <input
                        type="number"
                        min="80"
                        max="100"
                        value={settingsForm.alertThresholds.criticalPercentage}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          alertThresholds: {
                            ...settingsForm.alertThresholds,
                            criticalPercentage: parseInt(e.target.value)
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Custom Limits */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Custom Spending Limits</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Per Person Meal Limit ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={settingsForm.customLimits.perPersonMeal}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          customLimits: {
                            ...settingsForm.customLimits,
                            perPersonMeal: parseFloat(e.target.value)
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quarterly Physician Limit ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={settingsForm.customLimits.quarterlyPhysician}
                        onChange={(e) => setSettingsForm({
                          ...settingsForm,
                          customLimits: {
                            ...settingsForm.customLimits,
                            quarterlyPhysician: parseFloat(e.target.value)
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Notification Settings */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Notifications</h4>
                  <div className="space-y-3">
                    {Object.entries(settingsForm.notifications).map(([key, value]) => (
                      <label key={key} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => setSettingsForm({
                            ...settingsForm,
                            notifications: {
                              ...settingsForm.notifications,
                              [key]: e.target.checked
                            }
                          })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveComplianceSettings}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>Save Settings</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ComplianceMonitor);